import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUsers, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { fetchStaffEquipe, fetchStaffFirestore } from '../../api/fff';
import useInView from '../../hooks/useInView';
import PlayerCard from './PlayerCard';

export default function EquipeStaff({ clubId, equipeNom, equipeId }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const sliderRef = useRef(null);
  const [ref, inView] = useInView({ threshold: 0.1 });

  useEffect(() => {
    setLoading(true);
    setError(null);
    // Récupère staff Firestore + API, puis fusionne
    Promise.all([
      equipeId ? fetchStaffFirestore(equipeId) : Promise.resolve([]),
      fetchStaffEquipe(clubId, equipeNom)
    ])
      .then(([firestoreStaff, apiStaff]) => {
        // On évite les doublons (nom+prénom)
        const firestoreKeys = new Set(firestoreStaff.map(s => (s.nom||'').toLowerCase() + '|' + (s.prenom||'').toLowerCase()));
        const apiStaffFiltered = apiStaff.filter(s => !firestoreKeys.has((s.nom||'').toLowerCase() + '|' + (s.prenom||'').toLowerCase()));
        setStaff([...firestoreStaff, ...apiStaffFiltered]);
        setLoading(false);
      })
      .catch(() => {
        setError('Erreur lors du chargement du staff.');
        setLoading(false);
      });
  }, [clubId, equipeNom, equipeId]);

  const scroll = dir => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: dir * 340, behavior: 'smooth' });
    }
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8 }}
      className="w-full max-w-7xl mx-auto mt-12"
    >
      {/* Header avec glassmorphism */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="text-center mb-8"
      >
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-6 shadow-2xl mx-4">
          <div className="flex items-center justify-center gap-4 mb-2">
            <div className="bg-gradient-to-br from-[#FFE400] to-[#BDFF00] p-3 rounded-2xl shadow-lg">
              <FaUsers className="text-[#001B56] text-2xl" />
            </div>
            <h2 className="text-4xl font-title font-extrabold bg-gradient-to-r from-[#FFE400] to-[#BDFF00] bg-clip-text text-transparent">
              STAFF TECHNIQUE
            </h2>
          </div>
          <p className="text-white/80 font-accent text-lg">
            L'équipe dirigeante qui accompagne nos joueurs
          </p>
        </div>
      </motion.div>

      {/* Contenu principal */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 shadow-2xl mx-4"
      >
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center py-16"
            >
              {/* Spinner EFAFC */}
              <div className="relative mb-6">
                <div className="w-16 h-16 border-4 border-[#FFE400]/30 border-t-[#FFE400] rounded-full animate-spin"></div>
                <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-r-[#BDFF00] rounded-full animate-spin animate-reverse"></div>
              </div>
              <p className="text-[#FFE400] font-accent font-bold text-lg">
                Chargement du staff technique...
              </p>
            </motion.div>
          ) : error ? (
            <motion.div
              key="error"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="text-center py-16"
            >
              <div className="bg-red-500/20 backdrop-blur-sm border border-red-500/30 rounded-2xl p-6 max-w-md mx-auto">
                <div className="text-red-400 text-4xl mb-4">⚠️</div>
                <h3 className="text-red-400 font-title font-bold text-xl mb-2">
                  Erreur de chargement
                </h3>
                <p className="text-red-300 font-accent">
                  {error}
                </p>
              </div>
            </motion.div>
          ) : staff.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="text-center py-16"
            >
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 max-w-md mx-auto">
                <div className="text-[#FFE400] text-5xl mb-4">👥</div>
                <h3 className="text-[#FFE400] font-title font-bold text-xl mb-2">
                  Aucun staff disponible
                </h3>
                <p className="text-white/70 font-accent">
                  Les informations du staff technique ne sont pas encore disponibles.
                </p>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="relative"
            >
              {/* Navigation */}
              {staff.length > 2 && (
                <>
                  <motion.button
                    whileHover={{ scale: 1.1, x: -5 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => scroll(-1)}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-gradient-to-br from-[#FFE400] to-[#BDFF00] text-[#001B56] rounded-full p-4 shadow-2xl hover:shadow-[#FFE400]/25 transition-all duration-300 hidden md:flex items-center justify-center"
                    style={{ marginLeft: -60 }}
                    aria-label="Staff précédent"
                  >
                    <FaChevronLeft className="text-xl" />
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.1, x: 5 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => scroll(1)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-gradient-to-br from-[#FFE400] to-[#BDFF00] text-[#001B56] rounded-full p-4 shadow-2xl hover:shadow-[#FFE400]/25 transition-all duration-300 hidden md:flex items-center justify-center"
                    style={{ marginRight: -60 }}
                    aria-label="Staff suivant"
                  >
                    <FaChevronRight className="text-xl" />
                  </motion.button>
                </>
              )}

              {/* Slider horizontal */}
              <div
                ref={sliderRef}
                className="flex gap-8 overflow-x-auto scrollbar-hide px-4 py-4"
                style={{ scrollBehavior: 'smooth' }}
              >
                {staff.map((membre, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                    className="flex-shrink-0"
                  >
                    <PlayerCard 
                      joueur={{ 
                        nom: membre.nom, 
                        prenom: membre.prenom,
                        poste: membre.fonction || 'STAFF',
                        face_photo: membre.face_photo
                      }} 
                    />
                  </motion.div>
                ))}
              </div>

              {/* Indicateurs de scroll pour mobile */}
              {staff.length > 2 && (
                <div className="flex justify-center mt-6 gap-2 md:hidden">
                  {Array.from({ length: Math.ceil(staff.length / 2) }).map((_, idx) => (
                    <div
                      key={idx}
                      className="w-2 h-2 rounded-full bg-white/30"
                    />
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Effets de fond animés */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute top-1/4 left-1/4 w-32 h-32 bg-[#FFE400]/5 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            x: [0, -80, 0],
            y: [0, 60, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute bottom-1/4 right-1/4 w-40 h-40 bg-[#BDFF00]/5 rounded-full blur-3xl"
        />
      </div>
    </motion.div>
  );
} 